import {
  CLAVES_DE_FILTRO_SEGUIMIENTO,
  FILTROS_DE_COLUMNA_SEGUIMIENTO,
  esSalidaUrgente,
  type CampoEditable,
  type ConfiguracionDeVista,
  type ConsultaSeguimiento,
  type ExportacionResumen,
  type OrdenSeguimiento,
  type PaginaDeSeguimiento,
} from '@cnc/contracts';
import {useCallback, useState} from 'react';
import {Link, useSearchParams} from 'react-router';
import {ErrorDeApi} from '../api/cliente';
import {usePermisos} from '../componentes/sesion-contexto';
import {useColumnas} from '../componentes/usar-columnas';
import {
  consultaDeSeguimientoDesdeParametros,
  parametrosDeSeguimiento,
  rutaExcelDeSeguimiento,
  soloFiltrosDeSeguimiento,
  useAlertasDeSeguimiento,
  useCatalogosDeSeguimiento,
  useEliminarExportaciones,
  useFiltrosDeSeguimiento,
  useLoteDeCambios,
  useMapaDeSeguimiento,
  useOpcionesDeColumnaDeSeguimiento,
  useSeguimiento,
} from '../api/seguimiento';
import {FiltrosPorColumna} from '../componentes/filtros-por-columna';
import {columnasConFiltro, type UseOpcionesDeColumna} from '../utilidades/columnas-con-filtro';
import {AlertasSeguimiento} from '../componentes/alertas-seguimiento';
import {BarraDeVistas} from '../componentes/barra-de-vistas';
import {BotonDeEliminar} from '../componentes/boton-de-eliminar';
import {CambioEnLote} from '../componentes/cambio-en-lote';
import {esCampoEditable} from '../componentes/campos-exportacion';
import {CeldaEditable} from '../componentes/celda-editable';
import {DescargaEnFormatoBase} from '../componentes/descarga-formato-base';
import {COLUMNAS_DE_SEGUIMIENTO, DEFINICION_SEGUIMIENTO} from '../componentes/columnas-seguimiento';
import {mensajeDeExportaciones, revisionDeExportaciones} from '../componentes/eliminacion';
import {EstadoExportacion} from '../componentes/estado-exportacion';
import {FiltrosSeguimiento} from '../componentes/filtros-seguimiento';
import {MapaSeguimiento} from '../componentes/mapa-seguimiento';
import {Paginacion} from '../componentes/paginacion';
import {PestanasSeguimiento} from '../componentes/pestanas-seguimiento';
import {SelectorDeColumnas} from '../componentes/selector-de-columnas';
import {Tabla} from '../componentes/tabla';
import {ZonaAmpliable} from '../componentes/zona-ampliable';
import {ActualizarDesdeNavieras} from '../componentes/actualizar-desde-navieras';
import {tr} from '../i18n/idioma';
import {numero} from '../utilidades/formato';

type Columna = {id: string; valor: (fila: ExportacionResumen) => string};

export function PaginaSeguimiento() {
  const [parametros, setParametros] = useSearchParams();
  const consulta = consultaDeSeguimientoDesdeParametros(parametros);
  const filtrosActivos = soloFiltrosDeSeguimiento(consulta);
  const [columnas, cambiarColumnas] = useColumnas(DEFINICION_SEGUIMIENTO);
  const permisos = usePermisos();
  const [modoEdicion, setModoEdicion] = useState(false);
  const [rastreador, setRastreador] = useState(false);
  // Seleccion por identificador con su codigo; sobrevive al cambio de pagina.
  const [seleccion, setSeleccion] = useState<Map<number, string>>(new Map());
  // El panel de cambio en lote recuerda con que exportaciones se abrio, asi
  // el resultado sigue a la vista aunque la seleccion se limpie al aplicar.
  const [panelDeLote, setPanelDeLote] = useState<string[] | null>(null);
  const exportaciones = useSeguimiento(consulta);
  const filtros = useFiltrosDeSeguimiento(filtrosActivos);
  const alertas = useAlertasDeSeguimiento(filtrosActivos);
  const mapa = useMapaDeSeguimiento(filtrosActivos);
  const catalogos = useCatalogosDeSeguimiento();
  const lote = useLoteDeCambios();
  const eliminar = useEliminarExportaciones();
  // Valores de una columna para su filtro (se piden al abrir la lista).
  const useOpciones: UseOpcionesDeColumna = (columna, contiene, habilitado) =>
    useOpcionesDeColumnaDeSeguimiento(filtrosActivos, columna, contiene, habilitado);

  const cambiar = useCallback(
    (cambios: Partial<ConsultaSeguimiento>, conservarPagina = false) => {
      const actual = consultaDeSeguimientoDesdeParametros(new URLSearchParams(window.location.search));
      const nueva = {...actual, ...cambios, pagina: conservarPagina ? (cambios.pagina ?? actual.pagina) : 1};
      setParametros(parametrosDeSeguimiento(nueva), {replace: true});
    },
    [setParametros],
  );

  // Abrir una vista guardada: sus filtros y orden van a la direccion y sus columnas a la tabla.
  const aplicarVista = useCallback(
    (configuracion: ConfiguracionDeVista) => {
      setParametros(new URLSearchParams(configuracion.parametros), {replace: true});
      if (configuracion.columnas) cambiarColumnas(configuracion.columnas);
    },
    [setParametros, cambiarColumnas],
  );

  const ordenar = (orden: OrdenSeguimiento) => {
    const direccion = consulta.orden === orden && consulta.direccion === 'asc' ? 'desc' : 'asc';
    cambiar({orden, direccion}, true);
  };

  const alternarSeleccion = (filas: ExportacionResumen[], seleccionar: boolean) =>
    setSeleccion(actual => {
      const nueva = new Map(actual);
      for (const fila of filas) {
        if (seleccionar) nueva.set(fila.id, fila.codigo);
        else nueva.delete(fila.id);
      }
      return nueva;
    });

  // Edicion rapida: un campo de una exportacion por vez, por la misma puerta del lote.
  // Lleva el valor que la persona veia: si otra persona lo cambio entretanto,
  // no se guarda y el aviso queda arriba de la tabla (la tabla se actualiza).
  const [avisoDeEdicion, setAvisoDeEdicion] = useState<string | null>(null);
  const guardarCelda = async (codigo: string, campo: CampoEditable, valor: unknown, visto: unknown) => {
    try {
      await lote.mutateAsync({
        modo: 'aplicar',
        origen: 'tabla',
        omitirConErrores: false,
        crearSiNoExiste: false,
        elementos: [{codigo, cambios: {[campo]: valor}, ...(visto === undefined ? {} : {esperado: {[campo]: visto}})}],
      });
      setAvisoDeEdicion(null);
    } catch (error) {
      setAvisoDeEdicion(error instanceof Error ? error.message : tr('No se pudo guardar.', 'Could not save.'));
      void exportaciones.refetch();
      throw error;
    }
  };

  const celda = (columna: Columna, fila: ExportacionResumen) => {
    if (permisos.editar && modoEdicion && columna.id !== 'codigo' && esCampoEditable(columna.id)) {
      return (
        <CeldaEditable
          fila={fila}
          campo={columna.id}
          texto={columna.valor(fila)}
          catalogos={catalogos.data}
          onGuardar={guardarCelda}
        />
      );
    }
    return celdaDeLectura(columna, fila);
  };

  const boton = 'rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel';

  return (
    <section className="flex flex-col gap-4">
      <PestanasSeguimiento />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-verde-oscuro">
            {tr('Seguimiento de exportaciones', 'Export tracking')}
          </h1>
          <p className="text-sm text-apagado">
            {tr(
              'Exportaciones propias y de terceros con su logística.',
              'Own and third-party exports with their logistics.',
            )}
            {permisos.editar
              ? tr(
                  ' Se editan en el detalle, en la tabla (edición rápida), por lote o desde Excel; todo queda en el historial.',
                  ' Edit them in the detail page, in the table (quick edit), in batch or from Excel; everything is kept in the history.',
                )
              : tr(
                  ' Tu rol es de consulta: puedes filtrar, descargar y ver el detalle.',
                  ' Your role is read-only: you can filter, download and view the details.',
                )}
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          <DescargaEnFormatoBase informe="tracking" cadena={parametrosDeSeguimiento(consulta).toString()} />
          {permisos.editar && (
            <button
              type="button"
              onClick={() => setRastreador(v => !v)}
              aria-expanded={rastreador}
              className="rounded-md border border-verde bg-white px-3 py-1.5 text-sm text-verde-oscuro hover:bg-verde-claro"
            >
              {tr('🚢 Actualizar desde navieras', '🚢 Update from shipping lines')}
            </button>
          )}
          <button type="button" onClick={() => void exportaciones.refetch()} className={boton}>
            {tr('↻ Actualizar', '↻ Refresh')}
          </button>
          <SelectorDeColumnas definicion={DEFINICION_SEGUIMIENTO} visibles={columnas} onCambiar={cambiarColumnas} />
        </div>
      </div>

      {rastreador && <ActualizarDesdeNavieras onCerrar={() => setRastreador(false)} />}

      <BarraDeVistas
        pantalla="seguimiento"
        actual={{parametros: parametros.toString(), columnas, extra: {}}}
        onAplicar={aplicarVista}
      />

      {exportaciones.data && <Cifras pagina={exportaciones.data} />}

      <ZonaAmpliable titulo={tr('Seguimiento de exportaciones', 'Export tracking')}>
        <FiltrosSeguimiento
          consulta={consulta}
          filtros={filtros.data}
          onCambiar={cambiar}
          onLimpiar={() => cambiar(Object.fromEntries(CLAVES_DE_FILTRO_SEGUIMIENTO.map(k => [k, undefined])))}
          porColumna={
            <FiltrosPorColumna
              columnas={columnasConFiltro(
                columnas,
                COLUMNAS_DE_SEGUIMIENTO,
                FILTROS_DE_COLUMNA_SEGUIMIENTO,
                consulta.porColumna,
              )}
              valor={consulta.porColumna}
              onCambiar={porColumna => cambiar({porColumna})}
              useOpciones={useOpciones}
            />
          }
        />

        <div className="flex flex-wrap items-center gap-2 text-sm">
          {permisos.editar ? (
            <>
              <span className="text-xs font-semibold tracking-wide text-apagado uppercase">
                {tr('Actualizar datos', 'Update data')}
              </span>
              <Link
                to="/seguimiento/nueva"
                className="rounded-md border border-verde bg-verde px-3 py-1.5 text-white hover:bg-verde-oscuro"
              >
                {tr('+ Nueva exportación', '+ New export')}
              </Link>
              <button
                type="button"
                onClick={() => setModoEdicion(v => !v)}
                className={`rounded-md border px-3 py-1.5 ${
                  modoEdicion ? 'border-verde bg-verde-claro text-verde-oscuro' : 'border-linea bg-white hover:bg-papel'
                }`}
                title={tr(
                  'Edita cualquier celda directamente en la tabla: clic, escribe y Enter',
                  'Edit any cell directly in the table: click, type and press Enter',
                )}
              >
                {tr('✎ Edición rápida', '✎ Quick edit')} {modoEdicion ? tr('activa', 'on') : ''}
              </button>
              <button
                type="button"
                onClick={() => setPanelDeLote([...seleccion.values()].sort())}
                disabled={!seleccion.size}
                className="rounded-md border border-linea bg-white px-3 py-1.5 hover:bg-papel disabled:opacity-50"
                title={tr(
                  'Marca las exportaciones en la primera columna y cambia un mismo valor en todas',
                  'Tick the exports in the first column and set the same value on all of them',
                )}
              >
                {tr('⇶ Cambiar seleccionadas', '⇶ Change selected')}
                {seleccion.size ? ` (${numero(seleccion.size)})` : ''}
              </button>
              {seleccion.size > 0 && (
                <button type="button" onClick={() => setSeleccion(new Map())} className="text-apagado hover:text-tinta">
                  {tr('Quitar selección', 'Clear selection')}
                </button>
              )}
              <span className="mx-1 text-linea">|</span>
            </>
          ) : (
            <span className="text-xs font-semibold tracking-wide text-apagado uppercase">
              {tr('Descargar', 'Download')}
            </span>
          )}
          <a
            href={rutaExcelDeSeguimiento(consulta)}
            download
            className="rounded-md border border-linea bg-white px-3 py-1.5 hover:bg-papel"
            title={tr(
              'Descarga las exportaciones filtradas en un Excel con las columnas editables',
              'Download the filtered exports as an Excel file with the editable columns',
            )}
          >
            {tr('⬇ Excel para editar', '⬇ Editable Excel')}
          </a>
          {permisos.editar && (
            <Link
              to="/seguimiento/cambios"
              className="rounded-md border border-linea bg-white px-3 py-1.5 hover:bg-papel"
              title={tr(
                'Sube el Excel para editar con tus cambios o tu archivo de Status (Status Concentrates Shipments, hoja Tracking Status): verás qué cambia en cada exportación antes de guardar',
                'Upload the editable Excel with your changes or your Status file (Status Concentrates Shipments, Tracking Status sheet): you will see what changes in each export before saving',
              )}
            >
              {tr('⬆ Cargar cambios o Status', '⬆ Upload changes or Status')}
            </Link>
          )}
          {permisos.editar && modoEdicion && (
            <span className="text-xs text-apagado">
              {tr(
                'Clic en una celda, escribe y Enter guarda; Esc cancela. Cada cambio queda en el historial.',
                'Click a cell, type and press Enter to save; Esc cancels. Every change is kept in the history.',
              )}
            </span>
          )}
          {permisos.administrar && (
            <div className="ml-auto flex flex-wrap items-center gap-1.5 text-xs">
              <span className="text-apagado">🗑 {tr('Eliminar:', 'Delete:')}</span>
              <BotonDeEliminar
                etiqueta={`${tr('Seleccionadas', 'Selected')}${seleccion.size ? ` (${numero(seleccion.size)})` : ''}`}
                titulo={tr('Eliminar exportaciones', 'Delete exports')}
                deshabilitado={!seleccion.size}
                revisar={async () =>
                  revisionDeExportaciones(
                    await eliminar.mutateAsync({modo: 'revisar', codigos: [...seleccion.values()]}),
                  )
                }
                eliminar={async () =>
                  mensajeDeExportaciones(
                    await eliminar.mutateAsync({modo: 'eliminar', codigos: [...seleccion.values()]}),
                  )
                }
                onTerminado={() => setSeleccion(new Map())}
              />
              <BotonDeEliminar
                etiqueta={`${tr('Todas las filtradas', 'All filtered')}${exportaciones.data ? ` (${numero(exportaciones.data.total)})` : ''}`}
                titulo={tr('Eliminar todas las exportaciones filtradas', 'Delete all filtered exports')}
                deshabilitado={!exportaciones.data?.total}
                revisar={async () =>
                  revisionDeExportaciones(await eliminar.mutateAsync({modo: 'revisar', filtros: filtrosActivos}))
                }
                eliminar={async () =>
                  mensajeDeExportaciones(await eliminar.mutateAsync({modo: 'eliminar', filtros: filtrosActivos}))
                }
                onTerminado={() => setSeleccion(new Map())}
              />
            </div>
          )}
        </div>

        {avisoDeEdicion && (
          <div
            role="alert"
            className="flex items-start justify-between gap-3 rounded-lg border border-alerta/50 bg-white px-4 py-3 text-sm text-alerta"
          >
            <span>⚠ {avisoDeEdicion}</span>
            <button
              type="button"
              onClick={() => setAvisoDeEdicion(null)}
              className="text-xs text-apagado hover:text-tinta"
            >
              ✕ {tr('Cerrar', 'Close')}
            </button>
          </div>
        )}

        {permisos.editar && panelDeLote && panelDeLote.length > 0 && (
          <CambioEnLote
            codigos={panelDeLote}
            catalogos={catalogos.data}
            onCerrar={() => setPanelDeLote(null)}
            onAplicado={() => setSeleccion(new Map())}
          />
        )}

        {exportaciones.isError && (
          <div className="rounded-lg border border-alerta/30 bg-red-50 p-4 text-sm">
            <div className="font-medium text-alerta">
              {tr('No se pudo consultar el seguimiento', 'Could not load the tracking data')}
            </div>
            <div className="mt-1">{exportaciones.error.message}</div>
            {exportaciones.error instanceof ErrorDeApi && exportaciones.error.detalles?.length && (
              <ul className="mt-1 list-disc pl-5 text-xs text-apagado">
                {exportaciones.error.detalles.map(d => (
                  <li key={d}>{d}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <Tabla
          filas={exportaciones.data?.elementos ?? []}
          definicion={DEFINICION_SEGUIMIENTO}
          columnasVisibles={columnas}
          claveDeFila={fila => fila.id}
          orden={consulta.orden}
          direccion={consulta.direccion}
          onOrdenar={ordenar}
          onReordenar={cambiarColumnas}
          cargando={exportaciones.isFetching}
          mensajeVacio={tr(
            'No hay exportaciones que coincidan con los filtros seleccionados.',
            'No exports match the selected filters.',
          )}
          altoMaximo="calc(100vh - 28rem)"
          celda={celda}
          numeracionDesde={(consulta.pagina - 1) * consulta.porPagina}
          seleccion={
            permisos.editar ? {seleccionada: fila => seleccion.has(fila.id), onAlternar: alternarSeleccion} : undefined
          }
        />

        <Paginacion
          pagina={consulta.pagina}
          totalPaginas={exportaciones.data?.totalPaginas ?? 0}
          porPagina={consulta.porPagina}
          total={exportaciones.data?.total ?? 0}
          onPagina={pagina => cambiar({pagina}, true)}
          onPorPagina={porPagina => cambiar({porPagina})}
          unidad={{es: 'exportaciones', en: 'exports'}}
        />
      </ZonaAmpliable>

      <AlertasSeguimiento alertas={alertas.data} cargando={alertas.isFetching} />
      <MapaSeguimiento mapa={mapa.data} cargando={mapa.isFetching} />
    </section>
  );
}

// Todavia no zarpa (en bodega o en puerto de origen) y su ETD es de hoy a 3 dias.
const SIN_ZARPAR = new Set(['pending', 'stock', 'assigned', 'port_of_origin']);
function zarpaPronto(fila: ExportacionResumen) {
  if (!fila.etd || !SIN_ZARPAR.has(fila.estado)) return false;
  const hoy = new Intl.DateTimeFormat('en-CA', {timeZone: 'America/Bogota'}).format(new Date());
  return esSalidaUrgente(Math.round((Date.parse(fila.etd) - Date.parse(hoy)) / 86_400_000));
}

function celdaDeLectura(columna: Columna, fila: ExportacionResumen) {
  if (columna.id === 'codigo')
    return (
      <Link to={`/seguimiento/${encodeURIComponent(fila.codigo)}`} className="font-medium text-verde hover:underline">
        {fila.codigo}
      </Link>
    );
  if (columna.id === 'estado')
    return <EstadoExportacion estado={fila.estado} etiqueta={fila.estadoEtiqueta} final={fila.estadoFinal} />;
  if (columna.id === 'rastreo')
    return fila.enlaceNaviera ? (
      <a href={fila.enlaceNaviera} target="_blank" rel="noreferrer" className="text-verde hover:underline">
        {tr('Naviera ↗', 'Shipping line ↗')}
      </a>
    ) : (
      '—'
    );
  // Booking y ETD de las que zarpan en 3 dias o menos, con su propio color (Daniel, 7/10/2026).
  if ((columna.id === 'booking' || columna.id === 'etd') && zarpaPronto(fila))
    return (
      <span
        title={tr('Zarpa en 3 días o menos', 'Departs within 3 days')}
        className="rounded bg-urgente px-1.5 py-0.5 font-medium text-white"
      >
        {columna.valor(fila)}
      </span>
    );
  if (columna.id === 'bigBags' && fila.diferenciaBigBags)
    return (
      <span
        title={tr(
          `Las bolsas asignadas (${numero(fila.bigBags)}) no cuadran con lo reportado en el seguimiento (${numero(fila.bigBagsReportados)}).`,
          `The assigned bags (${numero(fila.bigBags)}) do not match what tracking reports (${numero(fila.bigBagsReportados)}).`,
        )}
        className="text-alerta"
      >
        {numero(fila.bigBags)} ⚠
      </span>
    );
  return undefined;
}

// Cifras de lo filtrado, como la franja de resumen de la app original.
function Cifras({pagina}: {pagina: PaginaDeSeguimiento}) {
  const r = pagina.resumen;
  const t = pagina.totales;
  const datos: Array<[string, string, string?]> = [
    [
      tr('Exportaciones', 'Exports'),
      numero(r.exportaciones),
      tr(`de ${numero(r.totalGeneral)}`, `of ${numero(r.totalGeneral)}`),
    ],
    [tr('En tránsito', 'In transit'), numero(r.enTransito)],
    [tr('Entregadas', 'Delivered'), numero(r.entregadas)],
    [tr('De terceros', 'Third party'), numero(r.deTerceros)],
    ['Big Bags', numero(t.bigBags)],
    [tr('Ton. secas', 'Dry tons'), numero(t.tonSecas, 2)],
    ['Au (oz)', numero(t.auOz, 2)],
    ['Ag (oz)', numero(t.agOz, 2)],
  ];
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8">
      {datos.map(([nombre, valor, secundario]) => (
        <div key={nombre} className="rounded-lg border border-linea bg-white px-3 py-2">
          <div className="text-xs text-apagado">{nombre}</div>
          <div className="text-base font-semibold tabular-nums text-verde-oscuro">
            {valor}
            {secundario && <span className="ml-2 text-sm font-medium text-apagado">{secundario}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}
